import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { COVER_EXPORT_PX } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { CoverCanvas } from './CoverCanvas';
import { FloatingToolbar } from './FloatingToolbar';
import { InlineTextEdit } from './InlineTextEdit';
import { StagePills } from './StagePills';
import { TextBoxLayer } from './TextBoxLayer';
import { COVER_SAFE_MARGIN, isOutsideSafeMargin, type SnapBox, type SnapResult } from './snapGuides';
import { useBoxPointer } from './useBoxPointer';
import { useCoverPicture } from './useCoverPicture';
import { useKitFontsReady } from './useKitFontsReady';
import { useStageBoxSizes } from './useStageBoxSizes';
import { useStageSize } from './useStageSize';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CoverStageProps {
  /** 1 = the cover fits the stage. */
  zoom: number;
  /** The Free, trial or public download has the watermark: the line under the cover says so. */
  hasWatermark: boolean;
}

type Guides = Pick<SnapResult, 'vertical' | 'horizontal'>;

/**
 * The stage (design cover-editor b1, d4): the square cover drawn by the one renderer, with the
 * text boxes as elements over it (select, drag, size, turn, type), the snap lines, the safe
 * margin, the small toolbar of the selected box and the three switches.
 */
const CoverStageComponent: React.FC<CoverStageProps> = ({ zoom, hasWatermark }) => {
  const intl = useIntl();
  const { state, dispatch, readOnly, setTool } = useCoverEditor();
  const { doc, selection } = state;
  const area = useRef<HTMLDivElement>(null);
  const cover = useRef<HTMLDivElement>(null);
  const side = Math.round(useStageSize(area) * zoom);
  const { picture, isFailed } = useCoverPicture(doc.background);
  const isFontsReady = useKitFontsReady();
  const sizes = useStageBoxSizes(doc.texts, side, isFontsReady);
  const [isSnapOn, setIsSnapOn] = useState(true);
  const [isSafeOn, setIsSafeOn] = useState(false);
  const [isSmallOn, setIsSmallOn] = useState(false);
  const [guides, setGuides] = useState<Guides | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const snapBoxOf = useCallback(
    (id: string): SnapBox | null => {
      const box = doc.texts.find((text) => text.id === id);
      const size = sizes[id];
      return box === undefined || size === undefined
        ? null
        : { x: box.x, y: box.y, width: size.width / side, height: size.height / side };
    },
    [doc.texts, side, sizes]
  );
  const sizeOf = useCallback(
    (box: { id: string }) => {
      const size = sizes[box.id];
      return { width: (size?.width ?? 0) / side, height: (size?.height ?? 0) / side };
    },
    [side, sizes]
  );
  const othersOf = useCallback(
    (id: string) =>
      doc.texts
        .filter((text) => text.id !== id)
        .map((text) => snapBoxOf(text.id))
        .filter((box): box is SnapBox => box !== null),
    [doc.texts, snapBoxOf]
  );
  const pointer = useBoxPointer({
    cover,
    dispatch,
    sizeOf,
    othersOf,
    isSnapOn,
    onGuides: setGuides,
    disabled: readOnly || editingId !== null,
  });

  const selected = useMemo(
    () => (selection?.kind === 'text' ? doc.texts.find((text) => text.id === selection.id) : undefined),
    [doc.texts, selection]
  );
  const editing = useMemo(() => doc.texts.find((text) => text.id === editingId), [doc.texts, editingId]);
  const selectedBox = selected === undefined ? null : snapBoxOf(selected.id);
  const showSafe = isSafeOn || guides !== null || (selectedBox !== null && isOutsideSafeMargin(selectedBox));

  const clearSelection = useCallback(() => dispatch({ type: 'select', selection: null }), [dispatch]);
  const startEdit = useCallback(
    (id: string) => {
      if (!readOnly) {
        setEditingId(id);
      }
    },
    [readOnly]
  );
  const endEdit = useCallback(
    (id: string, text: string) => {
      setEditingId(null);
      const box = doc.texts.find((item) => item.id === id);
      if (box === undefined || box.text === text) {
        return;
      }
      // A free box with no text is of no use; the title and the artist boxes stay for a new text.
      dispatch(
        text.trim() === '' && box.role === 'free'
          ? { type: 'removeText', id }
          : { type: 'updateText', id, patch: { text } }
      );
    },
    [dispatch, doc.texts]
  );
  const openText = useCallback(() => setTool('text'), [setTool]);

  return (
    <div className={styles.stage} ref={area} onPointerDown={clearSelection} role="presentation">
      <div className={styles.stageDots} />
      <div className={styles.art} style={{ width: side }}>
        <div className={styles.cover} ref={cover} style={{ width: side, height: side }}>
          <CoverCanvas
            doc={doc}
            side={side}
            picture={picture}
            isFontsReady={isFontsReady}
            hiddenTextId={editingId}
            className={styles.coverCanvas}
          />
          {showSafe && (
            <span className={styles.safe} style={{ inset: `${COVER_SAFE_MARGIN * 100}%` }}>
              <span className={styles.safeLabel}>{intl.formatMessage(coverEditorMessages.safeMargin)}</span>
            </span>
          )}
          {guides?.vertical.map((x) => (
            <span key={`v${x}`} className={styles.guideVertical} style={{ left: `${x * 100}%` }} />
          ))}
          {guides?.horizontal.map((y) => (
            <span key={`h${y}`} className={styles.guideHorizontal} style={{ top: `${y * 100}%` }} />
          ))}
          <TextBoxLayer
            texts={doc.texts}
            side={side}
            sizes={sizes}
            selectedId={selected?.id ?? null}
            editingId={editingId}
            onStart={pointer.start}
            onMove={pointer.move}
            onEnd={pointer.end}
            onEdit={startEdit}
          />
          {editing !== undefined && <InlineTextEdit box={editing} side={side} onDone={endEdit} />}
          {selected !== undefined && editingId === null && guides === null && !readOnly && (
            <FloatingToolbar
              box={selected}
              side={side}
              boxHeight={sizes[selected.id]?.height ?? 0}
              dispatch={dispatch}
              onOpenText={openText}
            />
          )}
        </div>
        <div className={styles.dimensions}>
          {isFailed
            ? intl.formatMessage(coverEditorMessages.pictureFailed)
            : withBrandWord(
                intl.formatMessage(
                  hasWatermark ? coverEditorMessages.dimensionsWatermark : coverEditorMessages.dimensions,
                  { size: COVER_EXPORT_PX }
                )
              )}
        </div>
      </div>
      <StagePills
        doc={doc}
        isSnapOn={isSnapOn}
        isSafeOn={isSafeOn}
        isSmallOn={isSmallOn}
        onToggleSnap={() => setIsSnapOn((on) => !on)}
        onToggleSafe={() => setIsSafeOn((on) => !on)}
        onToggleSmall={() => setIsSmallOn((on) => !on)}
      />
    </div>
  );
};

export const CoverStage = TypedMemo(CoverStageComponent);
